import { useStyles } from "@webapps/styles";
import { useState, useMemo } from "react";
import { format, parseISO, isWithinInterval, startOfMonth, endOfMonth, eachDayOfInterval, getDay, min, max, isSameDay, addMonths, isAfter, isBefore } from "date-fns";
import { fr } from "date-fns/locale";
import { css, cx } from "@emotion/css";
import { Delete, Edit, ChevronLeft, ChevronRight, Check } from "@material-ui/icons";
import { Button, IconButton } from "@material-ui/core";
import planningColors, { planningColorsText } from "pages/school/ClassesPage/Classes/colors/planningColors";
import { cpuUsage } from "process";
import { useHistory } from "react-router-dom";
import { ROUTES_PATH } from "config/constants";
import { useCalendar } from "pages/school/ClassesPage/ClassesDetail/Stores/CalendarProvider";
import { useRouter } from "@webapps/reactjs-tech-components";

const ScheduleSliderCalendar = ({
  month,
  holidays,
  monthPeriods,
  periods,
  isClassAuthorized,
  handleOpenModal,
  handleOpenDeleteModal,
  authorizeActions,
  startDate,
  endDate,
  from,
  calendarStyle,
  enableMonthNavigation = false,
  isDashboard = false,
  onMonthChange,
  onDeclareAbsence,
  setDate,
  configurationStatus,
  classroomId,
}) => {
  const {
    classes,
    css,
    cx,
    global,
    theme: { spacing },
  } = useStyles(styles);

  console.log("configurationStatus calendar", configurationStatus);

  const [currentMonth, setCurrentMonth] = useState(month);
  const { push, params } = useRouter();
  const { id: idParam } = params;

  const monthStart = new Date(startDate).getMonth();
  const currentMonthStart = new Date(currentMonth).getMonth();
  const monthEnd = new Date(endDate).getMonth();
  const currentMonthEnd = new Date(currentMonth).getMonth();

  const canGoToPreviousMonth = monthStart >= currentMonthStart
  const canGoToNextMonth = monthEnd <= currentMonthEnd

  const handlePreviousMonth = () => {

    if (!canGoToPreviousMonth) {
      const newMonth = addMonths(currentMonth, -1);
      setCurrentMonth(newMonth);
      onMonthChange?.(newMonth);
    }
  };

  const handleNextMonth = () => {


    if (!canGoToNextMonth) {
      const newMonth = addMonths(currentMonth, 1);
      setCurrentMonth(newMonth);
      onMonthChange?.(newMonth);
    }
  };

  const monthAndYear = format(currentMonth, "MMMM yyyy", { locale: fr });
  const firstDay = startOfMonth(currentMonth);
  const lastDay = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start: firstDay, end: lastDay });



  function capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }



  const handleSeePlanning = () => {
    push(`${ROUTES_PATH.SCHOOL_CLASSES_DETAIL}??planning=true`, { params: { id: idParam } });
  };

  // const handleDeclareAbsence = () => {
  //   if (onDeclareAbsence) {
  //     onDeclareAbsence();
  //   }
  // };

  return (
    <div className={cx(classes.calendarContainer, calendarStyle)}>
      <div>
        <div className={classes.headerContainer}>
          {enableMonthNavigation && (
            <Button
              variant="outlined"
              onClick={handlePreviousMonth}
              disabled={canGoToPreviousMonth}
              className={classes.navigationButton}
            >
              <ChevronLeft />
            </Button>
          )}
          <div className={cx(global.title, isDashboard && classes.dashboardHeader)}>
            {capitalizeFirstLetter(monthAndYear)}
          </div>
          {enableMonthNavigation && (
            <Button
              variant="outlined"
              onClick={handleNextMonth}
              disabled={canGoToNextMonth}
              className={classes.navigationButton}
            >
              <ChevronRight />
            </Button>
          )}
        </div>
        <div className={classes.weekDays}>
          {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map(day => (
            <div key={day} className={classes.weekDay}>{day}</div>
          ))}
        </div>
        <div className={classes.daysGrid}>
          {/* Ajoute des jours vides pour aligner le premier jour du mois */}
          {Array.from({ length: (getDay(firstDay) + 6) % 7 }).map((_, index) => (
            <div key={`empty-${index}`} className={classes.emptyDay}></div>
          ))}

          {daysInMonth.map((day, index) => {
            const dayString = format(day, "yyyy-MM-dd");

            // Trouver si ce jour appartient à une période
            const period = monthPeriods.find(p =>
              isWithinInterval(day, { start: parseISO(p.startDate), end: parseISO(p.endDate) })
            );

            return period && isFirstDayOfPeriod(dayString, period) ? (
              <PeriodGroup
                key={period.id}
                period={period}
                classes={classes}
                handleOpenDeleteModal={handleOpenDeleteModal}
                holidays={holidays}
                from={from}
                setDate={setDate}
                configurationStatus={configurationStatus}
              />
            ) : (
              !isInsidePeriod(dayString, monthPeriods) && (
                <Day
                  key={index}
                  day={day}
                  classes={classes}
                  isClassAuthorized={isClassAuthorized}
                  handleOpenModal={handleOpenModal}
                  holidays={holidays}
                  startDate={startDate}
                  endDate={endDate}
                  from={from}
                  authorizeActions={authorizeActions}
                  configurationStatus={configurationStatus}
                />
              )
            );
          })}
        </div>
      </div>
      {/* <div className={classes.buttonsContainer}>
        <Button
          variant="outlined"
          className={classes.actionButton}
          fullWidth
          onClick={() => push(`${ROUTES_PATH.SCHOOL_CLASSES_DETAIL}??planning=true`, { params: { id: classroomId } })}
        >
          Voir le planning complet
        </Button>
        <Button
          variant="outlined"
          className={cx(classes.actionButton, {
            cursor: new Date().getTime() > new Date(endDate).getTime() ? "not-allowed" : "pointer",
          })}
          fullWidth
          disabled={new Date().getTime() > new Date(endDate).getTime()}
          onClick={handleDeclareAbsence}
        >
          Déclarer une absence
        </Button>
      </div> */}
    </div>
  );
};

// ✅ Groupe les périodes par leur date de début
const groupPeriodsByStartDate = (periods, month) => {
  const grouped = {};
  periods.forEach((period) => {
    const start = parseISO(period.startDate);
    if (start.getMonth() === month.getMonth() && start.getFullYear() === month.getFullYear()) {
      grouped[format(start, "yyyy-MM-dd")] = period;
    }
  });
  return grouped;
};

// Vérifie si un jour est le premier jour d'une période
const isFirstDayOfPeriod = (dayString, period) => {
  return dayString === format(parseISO(period.startDate), "yyyy-MM-dd");
};

// Vérifie si un jour appartient à une période (évite de l'afficher en double)
const isInsidePeriod = (dayString, periods) => {
  return periods.some(period =>
    isWithinInterval(parseISO(dayString), {
      start: parseISO(period.startDate),
      end: parseISO(period.endDate),
    })
  );
};


// ✅ Composant qui affiche un groupe de jours pour une période
const PeriodGroup = ({ period, classes, handleOpenDeleteModal, holidays, from, setDate, configurationStatus }) => {

  const { state } = useCalendar();

  const days = eachDayOfInterval({ start: parseISO(period.startDate), end: parseISO(period.endDate) });
  const periodType = period.type;
  let colors = "";
  let colorsText = "";

  // regarder si day et dans holidays
  const isHoliday = (day) => {
    let formattedDay = format(day, "yyyy-MM-dd");
    const isTheDayHoliday = holidays.some(holiday => {
      if (holiday.date === formattedDay) {
        return true;
      }
    });

    return isTheDayHoliday;
  }

  const isHideVisibilityToggled = state.categoryTotals[periodType].visible === false;
  const isHolidayVisibilityToggled = state.categoryTotals.FERIES.visible === false;

  switch (periodType) {
    case "FERIES":
      colors = planningColors.FERIES;
      colorsText = planningColorsText.FERIES;
      break;
    case "VACANCES":
      colors = planningColors.VACANCES;
      colorsText = planningColorsText.VACANCES;
      break;
    case "ENTREPRISE":
      colors = "inherit";
      colorsText = "inherit";
      break;
    case "FORMATION":
      colors = planningColors.FORMATION;
      colorsText = planningColorsText.FORMATION;
      break;
    default:
      colors = "";
      colorsText = "";
  }

  const isDayConfigured = (day) => {
    return configurationStatus.some(status => status.date === format(day, "yyyy-MM-dd"));
  }

  return (
    <div
      className={classes.periodGroup}
      style={{ display: "contents" }}
    >
      {days.map((day, index) => {
        const isWeekend = day.getDay() === 6 || day.getDay() === 0;
        const isHolidayDay = isHoliday(day);
        const isConfigured = isDayConfigured(day);
        const startDay = isSameDay(day, new Date(period.realPeriodStartDate));
        const endDay = isSameDay(day, new Date(period.realPeriodEndDate));

        console.log("isHolidayDay : ", isHolidayDay, day);

        return (
          <div
            onClick={() => {
              setDate(format(day, "yyyy-MM-dd"))
            }}
            key={index}
            className={cx(classes.day, classes[period.type.toLowerCase()],
              css({
                backgroundColor: isHolidayDay
                  ? (isHolidayVisibilityToggled ? planningColors.FERIES + "4D !important" : planningColors.FERIES)
                  : isWeekend
                    ? "transparent !important"
                    : isHideVisibilityToggled
                      ? colors + "4D !important"
                      : colors,
                color: isHolidayDay ? planningColorsText.FERIES : isWeekend ? "black" : colorsText + "!important",
                cursor: from === "student" ? "default !important" : isWeekend ? "not-allowed" : "pointer",
                "&:hover": {
                  backgroundColor: isHolidayDay
                    ? planningColors.FERIES + "80 !important"
                    : isWeekend
                      ? "transparent"
                      : colors + "80 !important",
                },
              }),
              startDay && classes.startDay,
              endDay && classes.endDay
            )}
          >
            <div className={classes.dayContent}>
              {format(day, "d")}
              {isConfigured &&
                <div className={classes.configureCheck}>
                  <Check />
                </div>
              }
            </div>
          </div>
        )
      })}
    </div>
  );
};

// ✅ Composant pour afficher un jour sans période
const Day = ({ day, classes, isClassAuthorized, handleOpenModal, periods, startDate, endDate, holidays, from, authorizeActions }) => {
  const isHoliday = (day) => {
    let formattedDay = format(day, "yyyy-MM-dd");
    const isTheDayHoliday = holidays.some(holiday => {

      if (holiday.date === formattedDay) {
        return true;
      }
    });

    return isTheDayHoliday;
  }

  const isHolidayDay = isHoliday(day);
  const isWeekend = day.getDay() === 6 || day.getDay() === 0;
  const parsedStartDate = parseISO(startDate);
  const parsedEndDate = parseISO(endDate);
  const isOutsideRange = day < parsedStartDate || day > parsedEndDate;

  const { state, dispatch } = useCalendar();
  const isHolidayVisibilityToggled = state.categoryTotals.FERIES.visible === false;


  const handleDayClick = () => {
    if (from === "student") return;
    if (isClassAuthorized === false) return;
    if (isWeekend) return;
    if (isOutsideRange) return;
    if (authorizeActions.createPeriod === false) return;
    if (state.selectedByPeriod === false) {
      handleOpenModal({
        selectedStartDay: day,
        selectedEndDay: day,
      })
    };
    const normalizedDay = normalizeDate(day);



    // si on clique sur le même jour, on annule la sélection
    if (state.selectedStartDay && state.selectedStartDay.getTime() === normalizedDay.getTime()) {
      dispatch({ type: "RESET_SELECTION" });
      return;
    }

    if (!state.selectedStartDay) {
      // Première sélection
      dispatch({ type: "SELECT_START_DAY", payload: day });
      dispatch({ type: "SELECT_END_DAY", payload: day });
    } else if (day > state.selectedStartDay) {


      dispatch({ type: "SELECT_END_DAY", payload: day });
      handleOpenModal({
        selectedStartDay: state.selectedStartDay,
        selectedEndDay: state.selectedEndDay, // day pour l'asynchronisme
      })
    } else {
      // Réinitialise si l'utilisateur clique sur une date incorrecte
      dispatch({ type: "RESET_SELECTION" });

    }
  };

  const normalizeDate = (date) => {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
  };

  const dayIsInInterval = (day, start, end) => {
    return day >= start && day <= end;
  };

  const selectedDay = dayIsInInterval(day, state.selectedStartDay, state.selectedEndDay);


  return <div className={isHolidayDay ? css({
    textAlign: "center",
    backgroundColor: selectedDay ? "#F3F7FF" : isHolidayVisibilityToggled ? planningColors.FERIES + "4D !important" : planningColors.FERIES + " !important",
    padding: 12,
    cursor: from === "student" ? "default !important" : "auto",
    color: selectedDay ? "#152652" : isHolidayDay ? planningColorsText.FERIES : "#FFF",
    // transition: "0.3s",
    borderRadius: "50px",
    display: "flex",
    justifyContent: "center",
    border: "1px solid transparent",
    position: "relative",
    alignItems: "center",
    height: "32px",
  }) : cx(classes.day, css({
    "&:hover": {
      backgroundColor: from === "student" ? "inherit" : "#F3F7FF",
    },
  }))}
    onClick={() => from === "school" && handleDayClick()}

    style={{
      backgroundColor: isHolidayDay ? "red !important" : isWeekend ? "transparent " : selectedDay && "#F3F7FF",
      cursor: from === "student" ? "default !important" : isOutsideRange ? "not-allowed" : isWeekend ? "not-allowed" : "pointer",
      opacity: isOutsideRange && "30%"
    }}
  >{format(day, "d")}</div>;
};

// 🎨 **Styles corrigés**
const styles = ({ spacing }) => ({
  calendarContainer: {
    padding: "20px",
    backgroundColor: "#FFF",
    borderRadius: "10px",
    // boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
    width: "calc(33.33% - calc(40px / 3))",
    height: "350px",
    minWidth: "310px",
    fontFamily: "Montserrat, sans-serif",
    // media queries max 768px
    "@media (max-width: 1535px)": {
      width: "calc(50% - calc(40px / 3))",
    },
    "@media (max-width: 1200px)": {
      width: "calc(100% - calc(40px / 3))",
    },
    color: "black",
  },
  headerContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing(1),
    marginBottom: spacing(2)
  },
  dashboardHeader: {
    minWidth: "150px",
    textAlign: "center",
  },
  weekDays: {
    fontSize: "12px",
    fontFamily: "Roboto, sans-serif",
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    textAlign: "center",
    paddingBottom: spacing(1),
    color: "#5D75A8",
  },
  weekDay: {
    color: "#5A6DA1",
    padding: spacing(1),
  },
  daysGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)", // ✅ Assure une grille fixe de 7 colonnes
    // gap: spacing(1),
    paddingTop: spacing(1),
    gap: "12px 0px"
  },
  emptyDay: {
    visibility: "hidden",
  },
  day: {
    textAlign: "center",
    padding: spacing(1.5),
    // cursor: "pointer",
    // transition: "0.3s",
    border: "1px solid transparent",
    position: "relative",
    fontSize: "12px",
    fontFamily: "Roboto, sans-serif",
    height: "32px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",


  },
  dayContent: {
    position: "relative",
  },
  configureCheck: {
    position: "absolute",
    top: "-10px",
    right: "-25px",
    padding: "2px",
    backgroundColor: "#DBFAD8",
    borderRadius: "400px",
    border: "1px dashed #008000",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10,

    "svg": {
      fontSize: "12px",
      fill: "#008000",
    }
  },
  startDay: {
    borderTopLeftRadius: "100px",
    borderBottomLeftRadius: "100px",
  },
  endDay: {
    borderTopRightRadius: "100px",
    borderBottomRightRadius: "100px",
  },
  periodGroup: {
    display: "contents", // ✅ Permet de garder la structure de la grille
  },
  holidayDay: {
    textAlign: "center",
    // backgroundColor: planningColors.FERIES,
    padding: spacing(1.5),
    cursor: "not-allowed !important",
    color: "#FFF",
    // transition: "0.3s",
    borderRadius: "50px",
    display: "flex",
    justifyContent: "center",
    border: "1px solid transparent",
    position: "relative",
    alignItems: "center",
    fontSize: "12px",
    fontFamily: "Roboto, sans-serif",
  },
  modifyButton: {
    position: "absolute",
    top: "-24px",
    left: "0px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    // left: "50%",
    // transform: "translateX(-50%)",
    backgroundColor: "#fff",
    boxShadow: "rgba(0, 0, 0, 0.35) 0px 5px 15px",
    // disable the border
    border: "none",
    color: "black",
    borderRadius: "4px",
    padding: "4px 8px",
    fontSize: "15px",
    fontWeight: 500,
    color: "#152652",
    cursor: "pointer",
    zIndex: 10,
  },
  pencil: {
    marginRight: "5px",
  },
  navigationButton: {
    height: '26px',
    width: '26px',
    minWidth: '26px',
    padding: '0px',
    '&.Mui-disabled': {
      opacity: 0.3
    }
  },
  buttonsContainer: {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: spacing(2),
    marginTop: spacing(2),
  },
  actionButton: {
    padding: `${spacing(1)}px ${spacing(2)}px`,
  },
});

export default ScheduleSliderCalendar;
